import { motion } from "framer-motion";
import { Zap, Target, BarChart2, Shield } from "lucide-react";
import { LANDING } from "@/constants/testIds";

const benefits = [
  {
    icon: Zap,
    title: "Respuesta instantánea sin costo adicional",
    description:
      "Cada lead que escribe a las 11pm recibe atención inmediata. Sin horas extras, sin guardia humana. El asistente trabaja mientras tu equipo descansa.",
  },
  {
    icon: Target,
    title: "Asesoras enfocadas en cerrar ventas",
    description:
      "Las preguntas frecuentes las responde la IA. Las asesoras solo intervienen cuando hay una oportunidad real de venta o un caso especial.",
  },
  {
    icon: BarChart2,
    title: "Visibilidad total del funnel de captación",
    description:
      "Por primera vez, AEROBIC tendrá datos concretos: cuántas consultas llegan, qué preguntan, cuántas se convierten, y qué asesora cierra más.",
  },
  {
    icon: Shield,
    title: "Trazabilidad y cumplimiento con Meta",
    description:
      "Todas las conversaciones pasan por el sistema, respetando los términos de la API de Meta. Un número, todo controlado, nada se pierde.",
  },
];

export default function Benefits() {
  return (
    <section
      id="beneficios"
      data-testid={LANDING.benefitsSection}
      className="relative py-20 md:py-28 overflow-hidden"
    >
      {/* Background image with overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/7031705/pexels-photo-7031705.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
        }}
      />
      <div className="absolute inset-0 bg-gray-900/85" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="text-center mb-14"
        >
          <span className="inline-block text-[#38BDF8] text-sm font-semibold tracking-widest uppercase mb-3">
            Beneficios para AEROBIC
          </span>
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-700 text-white tracking-tight max-w-2xl mx-auto">
            Tecnología que trabaja para tu equipo, mientras tu equipo trabaja para tus socios
          </h2>
          <p className="mt-3 text-gray-400 text-base max-w-xl mx-auto">
            Un sistema de gestión completo, configurado con los procesos y la
            cultura de servicio de AEROBIC.
          </p>
        </motion.div>

        {/* Benefits grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {benefits.map((benefit, i) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl p-7 hover:bg-white/12 transition-colors"
            >
              <div className="w-10 h-10 bg-[#38BDF8]/15 rounded-xl flex items-center justify-center mb-4">
                <benefit.icon size={20} className="text-[#38BDF8]" />
              </div>
              <h3 className="font-['Outfit'] text-lg font-600 text-white mb-2">
                {benefit.title}
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.4 }}
          className="mt-14 grid grid-cols-3 gap-4 border-t border-white/15 pt-10"
        >
          {[
            { value: "7", label: "sedes de AEROBIC cubiertas" },
            { value: "100%", label: "de consultas atendidas" },
            { value: "1", label: "número de WhatsApp, todo centralizado" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="font-['Outfit'] text-3xl md:text-4xl font-800 text-[#38BDF8]">
                {stat.value}
              </p>
              <p className="text-gray-400 text-sm mt-1">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
